/* global BigInt */
export const MILEAGE_SETTINGS_ID = "mileage-settings";
export const mileageKey = value => {
  const text=String(value ?? "").trim().toUpperCase();
  return /^\d+$/.test(text) ? text.replace(/^0+(?=\d)/,"") : text;
};
export function normalizeMileageLookup(lookup = {}) {
  const entries=Object.entries(lookup);
  const normalized=Object.fromEntries(entries.map(([key,miles])=>[mileageKey(key),miles]));
  // Prefer an existing canonical code if older padded aliases have different values.
  entries.forEach(([key,miles])=>{if(key===mileageKey(key))normalized[key]=miles;});
  return normalized;
}
export function mileageNumber(value, kind) {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const precision = kind === "rate" ? 4 : 3;
  if (!new RegExp(`^\\d+(\\.\\d{1,${precision}})?$`).test(text)) throw new Error(`${kind === "rate" ? "Rate" : "Miles"} must be a nonnegative number with up to ${precision} decimal places.`);
  const number = Number(text);
  if (!Number.isFinite(number) || number > (kind === "rate" ? 1000 : 1000000)) throw new Error("Mileage value is too large.");
  return number;
}
export function parseMileageCSV(text, {onDuplicate} = {}) {
  const rows = [];let row = [],cell = "",quoted = false,closed = false;
  const source = String(text).replace(/^\uFEFF/, "");
  for(let index=0;index<source.length;index++) {
    const char = source[index];
    if (quoted) {
      if (char === '"' && source[index+1] === '"') {cell+='"';index++;}
      else if (char === '"') {quoted=false;closed=true;}
      else cell+=char;
    } else if (char === '"') {
      if (cell || closed) throw new Error("Invalid CSV quoting.");
      quoted=true;
    } else if (char === ',' || char === '\n' || char === '\r') {
      row.push(cell);cell="";closed=false;
      if(char !== ',') {rows.push(row);row=[];if(char==='\r' && source[index+1]==='\n')index++;}
    } else {if(closed && char.trim())throw new Error("Unexpected text after a quoted CSV field.");if(!closed)cell+=char;}
  }
  if(quoted)throw new Error("CSV has an unclosed quoted field.");
  row.push(cell);if(row.some(value=>value.trim()))rows.push(row);
  const nonempty = rows.filter(values=>values.some(value=>value.trim()));
  if(nonempty.length<2)throw new Error("CSV needs a header and at least one mileage row.");
  const headers=nonempty[0].map(value=>value.toLowerCase().replace(/[\s_-]/g,""));
  const idColumn=headers.findIndex(value=>value==='clientid');
  const milesColumn=headers.findIndex(value=>['miles','mileage','milestravelled','milestraveled'].includes(value));
  if(idColumn<0 || milesColumn<0)throw new Error('CSV needs columns named "Client ID" and "Miles".');
  const result = new Map();
  nonempty.slice(1).forEach((values,index)=>{
    if(values.length!==nonempty[0].length)throw new Error(`CSV row ${index+2} has the wrong number of columns. Quote IDs that contain commas.`);
    const key=mileageKey(values[idColumn]);
    if(!key)throw new Error(`Client ID is blank on row ${index+2}.`);
    const miles=mileageNumber(values[milesColumn],"miles");
    if(miles==null)throw new Error(`Miles are blank on row ${index+2}.`);
    if(result.has(key))onDuplicate?.(key);
    result.set(key,miles);
  });
  if(result.size>5000)throw new Error("Upload at most 5,000 client IDs per file.");
  return Object.fromEntries(result);
}
export function resolveMileage(row, settings = {}) {
  if(row.mileage) return row.mileage;
  const id = String(row.clientID ?? "").trim();
  const key=mileageKey(id);
  const lookup=settings.milesByClientID || {};
  const savedKey=key && Object.prototype.hasOwnProperty.call(lookup,key) ? key : Object.keys(lookup).find(code=>mileageKey(code)===key);
  const miles = savedKey ? lookup[savedKey] : null;
  return {clientID:id,miles,ratePerMile:settings.ratePerMile ?? null};
}
export function mileagePayout(row, settings = {}) {
  const data=resolveMileage(row,settings);
  if(data.miles==null || data.ratePerMile==null) return null;
  let miles,rate;
  try {miles=mileageNumber(data.miles,"miles");rate=mileageNumber(data.ratePerMile,"rate");} catch {return null;}
  return Number((BigInt(Math.round(miles*1000))*BigInt(Math.round(rate*10000))+50000n)/100000n);
}
